// The cell list: every object of one cell in one peek (v2-the-deep.md
// §15.6, R2658, R2708, R2851; ui-map-interface.md §8 "The small marks").
//
// A long press of a finger, or a second click on the cell of a desktop
// peek, lists what the cell holds: the sea objects, the nearest first, the
// pirate den of the cell, the point of interest of the survey, and every
// wreck of the wreck window on the cell. The wrecks the commander holds a
// cargo sighting of (R2843) stand as rows, the largest take first. The
// other wrecks fold into one line that counts them by side: `22 wrecks ·
// 14 Azure · 8 Crimson`. The rows take `list_page_rows` of
// `[chart.piece.wrecks.fig]` to a page with a `‹ 1/3 ›` pager, so the list
// fits one peek on a 390 px telephone. A row is a press: the peek then
// shows that object alone, with a press back to the list (R2708). A cell
// that holds one object peeks that object, as there is no list of one.
//
// Everything here is pure, so a test reads it with no overlay.

import { NpcKind, type DenInfo, type PointOfInterest, type Wreck } from '../../gen/proto/aquaphobia';
import { CHART } from '../../gen/presentation';
import { card as S } from '../../gen/strings';
import { type GameState, num } from '../../state';
import { denDesign, denForm, denRows } from '../../dens';
import { cellAt, factionName, mpxToPx } from '../../world';
import { esc } from '../esc';
import { cssColor, pctFig } from '../format';
import type { CardSpec } from './card';
import { denCard } from './dens';
import { classWord, poiCard, wreckCard } from './mapcards';
import { markSvg } from './marks';
import { seaObjectCard, type SeaObjectView } from './seaobjects';
import { cargoUnits, sightingOf, wreckTakes } from './treasure';

/** What the cell list reads of the state. */
export type CellState = GameState;

/** One object of a cell. `key` names it on the press of its row. */
export type CellItem =
  | { kind: 'sea'; key: string; sea: SeaObjectView }
  | { kind: 'den'; key: string; den: DenInfo }
  | { kind: 'poi'; key: string; poi: PointOfInterest }
  | { kind: 'wreck'; key: string; wreck: Wreck; units: number };

/** What one cell holds. */
export interface CellContents {
  /** The rows of the list: the sea objects, the den, the point and the
   *  sighted wrecks, in that order. */
  rows: CellItem[];
  /** The wrecks with no cargo sighting, which fold into one line. */
  folded: Wreck[];
  /** Every object of the cell: the rows and the folded wrecks. */
  total: number;
}

/** The act of a row of the cell list, and of the back press of one object
 *  shown from it (R2708): `data-id` is the key of the object, '' for the
 *  list. */
export const PEEK_OBJECT_ACT = 'peekobj';

/** The act of the pager of the cell list: `data-id` is the page. */
export const PEEK_PAGE_ACT = 'peekpage';

/** The key of a sea object, a den, a point and a wreck on a row press. */
function seaKey(id: number): string { return `o${id}`; }
export function denKey(den: number): string { return `d${den}`; }
export function poiKey(id: number): string { return `p${id}`; }
export function wreckKey(fleet: number): string { return `w${fleet}`; }

/** The wrecks of the wreck window in a cell, the newest first (R804).
 *  A store with no rules cuts no wreck. */
export function wrecksIn(s: Pick<GameState, 'wrecks' | 'rules' | 'seasonElapsedSec'>, cell: number): Wreck[] {
  if (cell < 0) return [];
  const span = s.rules.wreckWindowSec;
  const cut = span > 0 ? s.seasonElapsedSec() - span : -Infinity;
  const out: Wreck[] = [];
  for (const w of s.wrecks) {
    if (w.sunkSec >= cut && cellAt(mpxToPx(w.xMpx), mpxToPx(w.yMpx)) === cell) out.push(w);
  }
  return out.sort((a, b) => b.sunkSec - a.sunkSec || a.fleet - b.fleet);
}

/** The den of a cell that the den layer draws, or null. */
function denIn(s: CellState, cell: number): DenInfo | null {
  for (const d of denRows(s)) {
    if (d.cell === cell && denDesign(d) && denForm(d.state)) return d;
  }
  return null;
}

/**
 * What a cell holds (R2851). `sea` is the sea objects of the cell, the
 * nearest to the press first (`seaObjectsIn`). A point of interest counts
 * while it has a card: a seeded cache needs `treasure`.
 */
export function cellContents(s: CellState, cell: number, sea: readonly SeaObjectView[]): CellContents {
  const rows: CellItem[] = [];
  const folded: Wreck[] = [];
  if (cell < 0) return { rows, folded, total: 0 };
  for (const o of sea) rows.push({ kind: 'sea', key: seaKey(o.id), sea: o });
  const den = denIn(s, cell);
  if (den) rows.push({ kind: 'den', key: denKey(den.den), den });
  const poi = s.survey.poiAt(cell);
  if (poi && poiCard(s, poi, 'peek')) rows.push({ kind: 'poi', key: poiKey(poi.id), poi });
  const sighted: { wreck: Wreck; units: number }[] = [];
  // A recovered sighting (R2850) holds no cargo, so its wreck folds too.
  for (const w of wrecksIn(s, cell)) {
    const r = sightingOf(s, w.fleet);
    if (r && wreckTakes(s, w)) sighted.push({ wreck: w, units: r.take ? cargoUnits(r.take) : 0 });
    else folded.push(w);
  }
  // The largest take first; a tie keeps the newest wreck first.
  sighted.sort((a, b) => b.units - a.units);
  for (const k of sighted) rows.push({ kind: 'wreck', key: wreckKey(k.wreck.fleet), ...k });
  return { rows, folded, total: rows.length + folded.length };
}

/** The one object of a cell that holds one, as a row item, or null. */
export function onlyItem(c: CellContents): CellItem | null {
  if (c.total !== 1) return null;
  if (c.rows.length === 1) return c.rows[0];
  const w = c.folded[0];
  return { kind: 'wreck', key: wreckKey(w.fleet), wreck: w, units: 0 };
}

/** The peek card of one object, or null for a point with no card. */
export function itemCard(s: CellState, it: CellItem): CardSpec | null {
  switch (it.kind) {
    case 'sea': return seaObjectCard(s, it.sea, 'peek');
    case 'den': return denCard(s, it.den);
    case 'poi': return poiCard(s, it.poi, 'peek');
    case 'wreck': return wreckCard(s, it.wreck);
  }
}

/**
 * A peek with a press under its body that opens the cell list: the act of
 * the back press with no key, which shows the list (`Tooltip.focusObject`).
 * A second click and a long press open the same list.
 */
export function withListPress(spec: CardSpec, label: string): CardSpec {
  const open = `<button type="button" class="ck-solback" data-act="${PEEK_OBJECT_ACT}" data-id="">`
    + `${esc(label)}</button>`;
  return { ...spec, peekBody: (spec.peekBody ?? '') + open };
}

/** A peek that says how many more objects the cell holds, as a press
 *  that lists them. */
export function withMore(spec: CardSpec, more: number): CardSpec {
  return more > 0 ? withListPress(spec, S.so_list_more({ n: more })) : spec;
}

/**
 * The peek of one object a press picked from the cell list (R2708): its
 * card, with a press back to the list of the `n` objects of the cell
 * under its body.
 */
export function focusPeek(spec: CardSpec, n: number): CardSpec {
  return withListPress(spec, S.so_list_back({ n }));
}

/** One row of the list: the mark, the name with its kind line, and a
 *  figure. The row is a press (R2708). */
function row(key: string, mark: string, title: string, ink: string, sub: string, fig: string): string {
  return `<button type="button" class="ck-row ck-sor" data-act="${PEEK_OBJECT_ACT}" data-id="${key}">${mark}`
    + `<span class="ck-rn"><b style="color:${ink}">${esc(title)}</b><i>${esc(sub)}</i></span>`
    + `<span class="ck-rf">${fig}</span></button>`;
}

/** The ink of a wreck: the NPC ink, or the faction colour. */
function wreckInk(w: Wreck): string {
  return w.npc !== NpcKind.NPC_KIND_NONE ? 'var(--npc)' : cssColor(w.faction);
}

/** The side of a wreck as a word: the faction, or the NPC side. */
function wreckSide(w: Wreck): string {
  return w.npc !== NpcKind.NPC_KIND_NONE ? S.npc_side : factionName(w.faction);
}

/** The row of one object. */
function itemRow(s: CellState, it: CellItem): string {
  if (it.kind === 'wreck') {
    const w = it.wreck;
    return row(it.key, markSvg('wreckcargo', wreckInk(w)), classWord(s, w.cls), wreckInk(w),
      `${S.wreck_title} · ${wreckSide(w)}`, `${num(it.units)}<small> ${S.f_cargo}</small>`);
  }
  const c = itemCard(s, it);
  if (!c) return '';
  const sub = c.status ? `${c.kind ?? ''} · ${c.status.text}` : c.kind ?? '';
  if (it.kind === 'sea') {
    const o = it.sea;
    const hull = o.heard ? S.unknown : pctFig(o.hullPct);
    return row(it.key, c.mark ?? '', c.title, cssColor(o.faction), sub, `${hull}<small> ${S.so_list_hull}</small>`);
  }
  const ink = it.kind === 'den' ? 'var(--npc)' : 'var(--text)';
  return row(it.key, c.mark ?? '', c.title, ink, sub, '');
}

/**
 * The fold of the wrecks with no sighting: the count, then the count of
 * each side, the largest first, a tie by the faction number, the NPC side
 * last. `22 wrecks · 14 Azure · 8 Crimson`.
 */
export function foldLine(folded: readonly Wreck[]): string {
  if (folded.length === 0) return '';
  const by = new Map<number, number>();
  for (const w of folded) {
    const side = w.npc !== NpcKind.NPC_KIND_NONE ? -1 : w.faction;
    by.set(side, (by.get(side) ?? 0) + 1);
  }
  const sides = [...by.entries()].sort((a, b) => {
    if ((a[0] < 0) !== (b[0] < 0)) return a[0] < 0 ? 1 : -1;
    return b[1] - a[1] || a[0] - b[0];
  });
  const parts = [S.cl_fold({ n: folded.length })];
  for (const [side, n] of sides) {
    parts.push(S.cl_fold_side({ n, side: side < 0 ? S.npc_side : factionName(side) }));
  }
  return parts.join(' · ');
}

/** The rows of one page of the list. */
function pageRows(): number {
  return Math.max(1, CHART.pieces.wrecks.fig.list_page_rows);
}

/** The number of pages of a list. */
export function pageCount(c: CellContents): number {
  return Math.max(1, Math.ceil(c.rows.length / pageRows()));
}

/** The mark of the list: the mark of its first row, or the wreck. */
function listMark(s: CellState, c: CellContents): string {
  const first = c.rows[0];
  const spec = first ? itemCard(s, first) : null;
  return spec?.mark ?? markSvg('wreck', 'var(--text)');
}

/**
 * The cell peek of a long press, or of a second click (§15.6, R2851): the
 * count as the title, the rows of `page`, the pager while the rows fill
 * more than one page, and the fold of the wrecks with no sighting. The
 * list rides the peek body, so the peek of a telephone shows it whole.
 */
export function cellListPeek(s: CellState, c: CellContents, page: number): CardSpec {
  const per = pageRows();
  const pages = pageCount(c);
  const p = Math.min(Math.max(0, page), pages - 1);
  // The sighted wrecks stand under a head, so the reader sees why the
  // other wrecks fold: the head stands over the first wreck row of a page.
  const rows = c.rows.slice(p * per, p * per + per);
  let body = `<div class="ck-list ck-solist">${rows.map((it, i) => (it.kind === 'wreck' && rows[i - 1]?.kind !== 'wreck'
    ? `<div class="ck-clhead">${esc(S.cl_cargo_head)}</div>` : '') + itemRow(s, it)).join('')}`;
  if (pages > 1) {
    const btn = (to: number, glyph: string, title: string, off: boolean) =>
      `<button type="button" class="ck-clpg" data-act="${PEEK_PAGE_ACT}" data-id="${to}" title="${esc(title)}"`
      + `${off ? ' disabled' : ''}>${glyph}</button>`;
    body += `<div class="ck-clpager">${btn(p - 1, '‹', S.cl_prev_title, p === 0)}`
      + `<span>${esc(S.cl_page({ page: p + 1, pages }))}</span>${btn(p + 1, '›', S.cl_next_title, p === pages - 1)}</div>`;
  }
  const fold = foldLine(c.folded);
  if (fold) body += `<div class="ck-clfold">${esc(fold)}</div>`;
  body += '</div>';
  return {
    size: 'peek', mark: listMark(s, c), title: S.so_list_title({ n: c.total }), ink: 'neutral',
    kind: S.so_list_kind, close: false, peekBody: body,
  };
}
